import { downloadTorrent, getWebTorrentClient } from './torrent';
import { deleteFile } from './fileStorage';
import { getAllPins, getAllVideos, VideoMetadata } from './metadata';
import { getLikeCount } from './likes';

export type SignalType =
  | 'pin'
  | 'like'
  | 'save'
  | 'share'
  | 'replay'
  | 'watch_positive'
  | 'watch_negative'
  | 'dislike';

export interface SignalEntry {
  videoHash: string;
  signal: SignalType;
  magnetURI?: string;
  timestamp: number;
}

export interface InterestProfile {
  tags: Record<string, number>;
  creators: Record<string, number>;
}

export interface SeedingPrefs {
  enabled: boolean;
  onPin: boolean;
  onLike: boolean;
  onSave: boolean;
  onReplay: boolean;
  onWatch: boolean;
  removeSeedsOnDislike: boolean;
}

export interface SignalWeights {
  pin: number;
  like: number;
  save: number;
  share: number;
  replay: number;
  watch_positive: number;
  watch_negative: number;
  dislike: number;
}

export interface ScoredVideo extends VideoMetadata {
  algorithmScore: number;
  scoreBreakdown: {
    personalAffinity: number;
    socialSignal: number;
    recency: number;
  };
}

export interface AlgorithmConfig {
  gravity: number;       // HN-style recency decay exponent. 0.5=slow, 1.8=standard, 3.0=very fresh-biased
  maxPerAuthor: number;  // author diversity cap per feed page
  inNetworkRatio: number; // 0–1, fraction of feed from followed accounts (not yet enforced in retrieval)
  likeWeight: number;    // multiplier on like count in raw score
  commentWeight: number; // multiplier on comment count in raw score
}

const DEFAULT_ALGO_CONFIG: AlgorithmConfig = {
  gravity: 1.8,
  maxPerAuthor: 3,
  inNetworkRatio: 0.6,
  likeWeight: 2.0,
  commentWeight: 3.0,
};

const ALGO_CONFIG_KEY = 'authenticulture_algo_config';

export function getAlgorithmConfig(): AlgorithmConfig {
  try {
    const stored = localStorage.getItem(ALGO_CONFIG_KEY);
    return stored ? { ...DEFAULT_ALGO_CONFIG, ...JSON.parse(stored) } : { ...DEFAULT_ALGO_CONFIG };
  } catch {
    return { ...DEFAULT_ALGO_CONFIG };
  }
}

export function setAlgorithmConfig(config: Partial<AlgorithmConfig>): void {
  const current = getAlgorithmConfig();
  localStorage.setItem(ALGO_CONFIG_KEY, JSON.stringify({ ...current, ...config }));
}

const SIGNALS_KEY = 'authenticulture_signals';
const PROFILE_KEY = 'authenticulture_interest_profile';
const SEEDING_PREFS_KEY = 'authenticulture_seeding_prefs';
const WEIGHTS_KEY = 'authenticulture_signal_weights';
const SIGNAL_RING_SIZE = 1000;

const DEFAULT_WEIGHTS: SignalWeights = {
  pin: 4,
  like: 3,
  save: 2,
  share: 2,
  replay: 2,
  watch_positive: 1,
  watch_negative: -1,
  dislike: -3,
};

const DEFAULT_SEEDING_PREFS: SeedingPrefs = {
  enabled: true,
  onPin: true,
  onLike: true,
  onSave: true,
  onReplay: true,
  onWatch: true,
  removeSeedsOnDislike: true,
};

export function getSignalWeights(): SignalWeights {
  const stored = localStorage.getItem(WEIGHTS_KEY);
  return stored ? { ...DEFAULT_WEIGHTS, ...JSON.parse(stored) } : { ...DEFAULT_WEIGHTS };
}

export function setSignalWeights(weights: Partial<SignalWeights>): void {
  const current = getSignalWeights();
  localStorage.setItem(WEIGHTS_KEY, JSON.stringify({ ...current, ...weights }));
}

export function getSeedingPrefs(): SeedingPrefs {
  const stored = localStorage.getItem(SEEDING_PREFS_KEY);
  return stored ? { ...DEFAULT_SEEDING_PREFS, ...JSON.parse(stored) } : { ...DEFAULT_SEEDING_PREFS };
}

export function setSeedingPrefs(prefs: Partial<SeedingPrefs>): void {
  const current = getSeedingPrefs();
  localStorage.setItem(SEEDING_PREFS_KEY, JSON.stringify({ ...current, ...prefs }));
}

export function getInterestProfile(): InterestProfile {
  const stored = localStorage.getItem(PROFILE_KEY);
  return stored ? JSON.parse(stored) : { tags: {}, creators: {} };
}

function saveInterestProfile(profile: InterestProfile): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function clearInterestProfile(): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify({ tags: {}, creators: {} }));
}

export function resetTagInProfile(tag: string): void {
  const profile = getInterestProfile();
  delete profile.tags[tag];
  saveInterestProfile(profile);
}

function getSignalLog(): SignalEntry[] {
  const stored = localStorage.getItem(SIGNALS_KEY);
  return stored ? JSON.parse(stored) : [];
}

function appendSignalLog(entry: SignalEntry): void {
  const log = getSignalLog();
  log.push(entry);
  if (log.length > SIGNAL_RING_SIZE) log.splice(0, log.length - SIGNAL_RING_SIZE);
  localStorage.setItem(SIGNALS_KEY, JSON.stringify(log));
}

function updateInterestProfile(videoHash: string, weight: number): void {
  const video = getAllVideos().find(v => v.hash === videoHash);
  if (!video) return;
  const profile = getInterestProfile();
  video.tags.forEach(tag => {
    profile.tags[tag] = (profile.tags[tag] || 0) + weight;
  });
  if (video.uploader) {
    profile.creators[video.uploader] = (profile.creators[video.uploader] || 0) + weight * 1.5;
  }
  saveInterestProfile(profile);
}

function parseMagnetHash(magnetURI: string): string | null {
  const match = magnetURI.match(/xt=urn:btih:([a-fA-F0-9]{40}|[A-Z2-7]{32})/i);
  return match ? match[1].toLowerCase() : null;
}

async function applySeeding(signal: SignalType, magnetURI?: string): Promise<void> {
  const prefs = getSeedingPrefs();
  if (!prefs.enabled || !magnetURI) return;

  if (signal === 'dislike' && prefs.removeSeedsOnDislike) {
    const client = getWebTorrentClient();
    const torrent = client.get(magnetURI);
    if (torrent) torrent.destroy();
    const hash = parseMagnetHash(magnetURI);
    if (hash) await deleteFile(hash).catch(() => {});
    return;
  }

  const seedingSignalMap: Partial<Record<SignalType, keyof SeedingPrefs>> = {
    pin: 'onPin',
    like: 'onLike',
    save: 'onSave',
    replay: 'onReplay',
    watch_positive: 'onWatch',
  };

  const prefKey = seedingSignalMap[signal];
  if (!prefKey || !prefs[prefKey as keyof SeedingPrefs]) return;

  const client = getWebTorrentClient();
  if (!client.get(magnetURI)) {
    downloadTorrent(magnetURI).catch(() => {});
  }
}

export async function recordSignal(
  videoHash: string,
  signal: SignalType,
  magnetURI?: string
): Promise<void> {
  const weights = getSignalWeights();
  const weight = weights[signal];
  appendSignalLog({ videoHash, signal, magnetURI, timestamp: Date.now() });
  updateInterestProfile(videoHash, weight);
  await applySeeding(signal, magnetURI);
}

export function getSignalHistory(videoHash: string): SignalEntry[] {
  return getSignalLog().filter(e => e.videoHash === videoHash);
}

function computePersonalAffinity(video: VideoMetadata, profile: InterestProfile): number {
  let score = 0;
  video.tags.forEach(tag => { score += profile.tags[tag] || 0; });
  score += (profile.creators[video.uploader] || 0);
  return Math.max(0, Math.min(score / 20, 1));
}

function ageHours(timestamp: number): number {
  return (Date.now() - timestamp) / 3_600_000;
}

function gravityDecay(timestamp: number, gravity: number): number {
  return 1 / Math.pow(ageHours(timestamp) + 2, gravity);
}

function computeSocialSignal(videoHash: string): number {
  const window48h = Date.now() - 48 * 3_600_000;
  const recentPins = getAllPins().filter(p => p.videoHash === videoHash && p.pinnedAt > window48h);
  return Math.min(recentPins.length / 20, 1);
}

export function applyAuthorCap<T>(items: T[], getAuthor: (item: T) => string, max: number): T[] {
  const counts = new Map<string, number>();
  return items.filter(item => {
    const author = getAuthor(item);
    const count = counts.get(author) ?? 0;
    if (count >= max) return false;
    counts.set(author, count + 1);
    return true;
  });
}

function scoreVideo(video: VideoMetadata, profile: InterestProfile, config: AlgorithmConfig): ScoredVideo {
  const personalAffinity = computePersonalAffinity(video, profile);
  const socialSignal = computeSocialSignal(video.hash);
  const likeScore = Math.min(getLikeCount(video.hash) * config.likeWeight / 20, 1);
  const rawScore = personalAffinity * 0.5 + socialSignal * 0.2 + likeScore * 0.3;
  const algorithmScore = rawScore * gravityDecay(video.uploadedAt, config.gravity);
  return { ...video, algorithmScore, scoreBreakdown: { personalAffinity, socialSignal, recency: gravityDecay(video.uploadedAt, config.gravity) } };
}

export function getForYouFeed(limit = 50): ScoredVideo[] {
  const config = getAlgorithmConfig();
  const profile = getInterestProfile();
  const scored = getAllVideos()
    .map(v => scoreVideo(v, profile, config))
    .sort((a, b) => b.algorithmScore - a.algorithmScore);
  return applyAuthorCap(scored, v => v.uploader, config.maxPerAuthor).slice(0, limit);
}

export function getLocalFeed(city: string, limit = 50): ScoredVideo[] {
  const config = getAlgorithmConfig();
  const profile = getInterestProfile();
  const scored = getAllVideos()
    .filter(v => typeof (v as any).city === 'string' && (v as any).city.toLowerCase() === city.toLowerCase())
    .map(v => scoreVideo(v, profile, config))
    .sort((a, b) => b.algorithmScore - a.algorithmScore);
  return applyAuthorCap(scored, v => v.uploader, config.maxPerAuthor).slice(0, limit);
}

export function getTrendingFeed(limit = 50): VideoMetadata[] {
  const videos = getAllVideos();
  const window48h = Date.now() - 48 * 3_600_000;
  const allPins = getAllPins();

  return videos
    .map(video => ({
      ...video,
      recentPinCount: allPins.filter(p => p.videoHash === video.hash && p.pinnedAt > window48h).length,
    }))
    .sort((a, b) => b.recentPinCount - a.recentPinCount)
    .slice(0, limit);
}
